import { createStyles, makeStyles } from "@material-ui/core";
import classnames from "classnames";
import * as React from "react";

import {
    baseColors,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    EnvelopeIcon,
    FacebookIcon,
    Input,
    LinkedInIcon,
    RelodeTheme,
    SelectableButtonItem,
    SelectableButtonsList,
    Text,
    TwitterIcon,
    useDeviceSize,
} from "../../../src";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                minWidth: 640,
            },
            dialogTitle: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
            },
            infoText: {
                color: theme.palette.grey[500],
            },
            input: {
                margin: theme.spacing(0, 2, 0, 0),
                flex: 1,
            },
            urlShare: {
                display: "flex",
                margin: theme.spacing(2, 0),
            },
            shareActions: {
                display: "flex",
                marginBottom: theme.spacing(2),
            },
            buttonEmail: {
                background: theme.relode.colors.socialShare.email.main,
                border: "1px solid #eee",
                color: baseColors.navy,
                marginBottom: theme.spacing(2),
                "&:hover": {
                    color: baseColors.navy,
                    background: theme.relode.colors.socialShare.email.light,
                },
            },
            buttonFacebook: {
                background: theme.relode.colors.socialShare.facebook.main,
                color: theme.palette.common.white,
                marginBottom: theme.spacing(2),
                "&:hover": {
                    color: theme.palette.common.white,
                    background: theme.relode.colors.socialShare.facebook.light,
                },
            },
            buttonLinkedIn: {
                background: theme.relode.colors.socialShare.linkedin.main,
                color: theme.palette.common.white,
                marginBottom: theme.spacing(2),
                "&:hover": {
                    color: theme.palette.common.white,
                    background: theme.relode.colors.socialShare.linkedin.light,
                },
            },
            buttonTwitter: {
                background: theme.relode.colors.socialShare.twitter.main,
                color: theme.palette.common.white,
                marginBottom: theme.spacing(2),
                "&:hover": {
                    color: theme.palette.common.white,
                    background: theme.relode.colors.socialShare.twitter.light,
                },
            },
            shareButtons: {
                display: "flex",
                justifyContent: "space-between",
                [theme.breakpoints.down("sm")]: {
                    flexDirection: "column",
                },
            },
            buttonCopy: {},
            icon: {
                marginRight: theme.spacing(1),
            },
            jobName: {
                paddingLeft: 24,
                marginBottom: theme.spacing(2),
            },
            [theme.breakpoints.down("sm")]: {
                root: {
                    minWidth: 0,
                    width: "100%",
                },
                urlShare: {
                    flexDirection: "column",
                },
                buttonEmail: {
                    width: "100%",
                },
                buttonCopy: {
                    marginTop: theme.spacing(2),
                },
                infoText: {
                    textAlign: "center",
                },
            },
        }),
    { name: "shareDialog" }
);

interface JobShareDialogProps {
    onCancel: () => void;
    publicJobShareInfo?: {
        name: string;
        emailUrl: string;
        shortenedUrl: string;
        facebookUrl?: string;
        linkedInUrl?: string;
        twitterUrl?: string;
    };
    recruiterJobShareInfo?: {
        name: string;
        emailUrl: string;
        shortenedUrl: string;
    };
}

export const JobShareDialog = (props: JobShareDialogProps) => {
    const { onCancel, publicJobShareInfo, recruiterJobShareInfo } = props;
    const { name } = publicJobShareInfo;
    const device = useDeviceSize();

    const classes = useStyles(props);
    const fileInput = React.useRef<HTMLInputElement>();
    const [textCopied, setTextCopied] = React.useState(false);
    const [displayedTab, setDisplayedTab] = React.useState<"public" | "ta">(
        () => "public"
    );

    const handleCloseDialog = () => {
        setTextCopied(false);
        onCancel();
    };

    const handleCopy = () => {
        fileInput.current && fileInput.current.select();
        document.execCommand("copy");
        handleCopySuccess();
    };

    const handleCopySuccess = () => {
        setTextCopied(true);
    };

    const handleButtonFieldChange = (item: SelectableButtonItem) => {
        setDisplayedTab(item.id);
    };

    return (
        <Dialog open onClose={handleCloseDialog}>
            <DialogTitle title="share job" onClose={handleCloseDialog} />
            <Text className={classes.jobName} variant="h4">
                {name}
            </Text>
            <DialogContent>
                {publicJobShareInfo && recruiterJobShareInfo ? (
                    <SelectableButtonsList
                        id="jobShareType"
                        size="md"
                        onClick={handleButtonFieldChange}
                        items={[
                            { id: "public", label: "public" },
                            { id: "ta", label: "recruiters" },
                        ].map(tab => ({
                            id: tab.id,
                            label: tab.label,
                            selected: tab.id === displayedTab,
                        }))}
                    />
                ) : null}

                <div className={classes.urlShare}>
                    <Input
                        data-cy="shareLink"
                        inputRef={fileInput}
                        value={
                            displayedTab === "ta"
                                ? recruiterJobShareInfo.shortenedUrl
                                : publicJobShareInfo.shortenedUrl
                        }
                        className={classes.input}
                        autoFocus={true}
                    />
                    <Button
                        data-cy="copyButton"
                        size="small"
                        color="primary"
                        variant="contained"
                        onClick={handleCopy}
                        className={classnames(classes.buttonCopy)}
                        fullWidth={device === "mobile"}
                    >
                        {textCopied ? "copied" : "copy"}
                    </Button>
                </div>
                <p className={classes.infoText}>
                    share via email{" "}
                    {(!!publicJobShareInfo.facebookUrl ||
                        !!publicJobShareInfo.linkedInUrl ||
                        !!publicJobShareInfo.twitterUrl) &&
                    displayedTab === "public"
                        ? "or social media"
                        : null}
                </p>
                <div className={classes.shareButtons}>
                    <Button
                        variant="contained"
                        href={
                            displayedTab === "ta"
                                ? recruiterJobShareInfo.emailUrl
                                : publicJobShareInfo.emailUrl
                        }
                        color="default"
                        size="small"
                        fullWidth={device === "mobile"}
                        className={classnames(classes.buttonEmail)}
                    >
                        <EnvelopeIcon className={classes.icon} /> email
                    </Button>
                    {!!publicJobShareInfo.facebookUrl &&
                    displayedTab === "public" ? (
                        <Button
                            variant="contained"
                            target="_blank"
                            href={publicJobShareInfo.facebookUrl}
                            color="default"
                            size="small"
                            fullWidth={device === "mobile"}
                            className={classnames(classes.buttonFacebook)}
                        >
                            <FacebookIcon className={classes.icon} /> post
                        </Button>
                    ) : null}
                    {!!publicJobShareInfo.linkedInUrl &&
                    displayedTab === "public" ? (
                        <Button
                            variant="contained"
                            target="_blank"
                            href={publicJobShareInfo.linkedInUrl}
                            color="default"
                            size="small"
                            fullWidth={device === "mobile"}
                            className={classnames(classes.buttonLinkedIn)}
                        >
                            <LinkedInIcon className={classes.icon} /> share
                        </Button>
                    ) : null}
                    {!!publicJobShareInfo.twitterUrl &&
                    displayedTab === "public" ? (
                        <Button
                            variant="contained"
                            target="_blank"
                            href={publicJobShareInfo.twitterUrl}
                            color="default"
                            size="small"
                            fullWidth={device === "mobile"}
                            className={classnames(classes.buttonTwitter)}
                        >
                            <TwitterIcon className={classes.icon} /> tweet
                        </Button>
                    ) : null}
                </div>
            </DialogContent>
        </Dialog>
    );
};
